import { groupBy } from "./groupBy";
import { filterData } from "../../data/filterEngine";
import { computeTrend } from "./trend";

// ============================================================
// DRIVER / "WHY" ANALYSIS
// ============================================================
// Answers "why did X change" by comparing dimension-level breakdowns
// between two periods and ranking which dimension VALUES account for the
// largest share of the total change. This is observational decomposition,
// NOT causal inference — the result is always framed as "associated with"
// / "largest contributors", never "caused by". The caller (resultFormatter)
// is responsible for keeping that distinction in the final text.

function periodFilter(dataset, dateColumn, period) {
  return filterData(dataset, [{ column: dateColumn, operator: "=", value: period, datePart: "year" }]);
}

// Ranks how much each value of one dimension contributed to the total
// change in `measure` between periodA and periodB.
function analyzeDimension(dataA, dataB, dimension, measure) {

  const groupedA = groupBy(dataA, dimension, measure, "sum");
  const groupedB = groupBy(dataB, dimension, measure, "sum");

  const mapA = Object.fromEntries(groupedA.map(g => [g.group, g.value || 0]));
  const mapB = Object.fromEntries(groupedB.map(g => [g.group, g.value || 0]));

  const allKeys = new Set([...Object.keys(mapA), ...Object.keys(mapB)]);

  const contributions = [...allKeys].map(key => {
    const valueA = mapA[key] ?? 0;
    const valueB = mapB[key] ?? 0;
    return {
      label: key,
      valueA,
      valueB,
      change: valueB - valueA,
    };
  });

  // Rank by magnitude of change (biggest movers first, regardless of sign)
  contributions.sort((a, b) => Math.abs(b.change) - Math.abs(a.change));

  return contributions;
}

export function computeDriverAnalysis(dataset, {
  measure,
  dateColumn,
  periodA,
  periodB,
  dimensions = [],
} = {}) {

  if (!measure) {
    return { success: false, error: "Driver analysis requires a measure." };
  }

  if (!dateColumn) {
    return { success: false, error: "Driver analysis requires a time dimension to compare periods." };
  }

  // Default to the two most recent periods in the trend if not specified.
  let resolvedA = periodA;
  let resolvedB = periodB;

  if (!resolvedA || !resolvedB) {

    const trend = computeTrend(dataset, { dateColumn, measure, period: "year", aggregation: "sum" });

    if (!trend.success || trend.series.length < 2) {
      return { success: false, error: "Not enough time periods available to compare." };
    }

    resolvedA = resolvedA || trend.series[trend.series.length - 2].period;
    resolvedB = resolvedB || trend.series[trend.series.length - 1].period;
  }

  const dataA = periodFilter(dataset, dateColumn, resolvedA);
  const dataB = periodFilter(dataset, dateColumn, resolvedB);

  if (!dataA.length || !dataB.length) {
    return { success: false, error: `No data available for one of the compared periods (${resolvedA}, ${resolvedB}).` };
  }

  const totalA = dataA.reduce((s, r) => s + (Number(r[measure]) || 0), 0);
  const totalB = dataB.reduce((s, r) => s + (Number(r[measure]) || 0), 0);
  const totalChange = totalB - totalA;
  const totalPctChange = totalA ? (totalChange / Math.abs(totalA)) * 100 : null;

  // Volume vs. transaction-size decomposition (same caution-guard logic used
  // in the report engine) — the strongest, most defensible evidence we can
  // offer without overclaiming causation.
  const countA = dataA.length;
  const countB = dataB.length;
  const avgA = countA ? totalA / countA : null;
  const avgB = countB ? totalB / countB : null;

  const countChangePct = countA ? ((countB - countA) / countA) * 100 : null;
  const avgChangePct = (avgA && avgB !== null) ? ((avgB - avgA) / Math.abs(avgA)) * 100 : null;

  const dimensionContributions = dimensions
    .map(dimension => {

      const contributions = analyzeDimension(dataA, dataB, dimension, measure);

      if (!contributions.length) return null;

      // Only the top 3 movers per dimension — the rest isn't meaningful evidence.
      const topContributors = contributions.slice(0, 3).map(c => ({
        label: c.label,
        valueA: c.valueA,
        valueB: c.valueB,
        change: c.change,
        shareOfTotalChange: totalChange ? (c.change / totalChange) * 100 : null,
      }));

      return { dimension, topContributors };
    })
    .filter(Boolean);

  return {
    success: true,
    tool: "driver",
    measure,
    dateColumn,
    periodA: resolvedA,
    periodB: resolvedB,
    totalA,
    totalB,
    totalChange,
    totalPctChange,
    direction: totalChange > 0 ? "increased" : totalChange < 0 ? "decreased" : "stable",
    countChangePct,
    avgChangePct,
    dimensionContributions,
  };
}
